import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, LayoutDashboard, User, Heart, Shield, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getInitials, getAvatarColor, getUserPictureUrl } from '../utils/avatar';

/**
 * FloatingMenuFAB (iter95) — renamed to "Profile".
 *
 * Bottom FAB in the stack (below Messages FAB). Shows the user's Google
 * profile picture when available, otherwise initials on a name-hashed
 * colour circle (no gender/demographic collection).
 *
 * Menu order: My Account → Dashboard → Wishlist → Admin (admin only) → Logout.
 * Login/Sign-up shown when user is signed out.
 */

const FloatingMenuFAB = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const pictureUrl = getUserPictureUrl(user);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const go = (path) => {
    setOpen(false);
    navigate(path);
  };

  const handleLogout = async () => {
    setOpen(false);
    try { await logout(); } catch (_) { /* ignore */ }
    navigate('/');
  };

  // Order per product requirements: My Account, Dashboard, Wishlist, Admin (admin only).
  const items = user ? [
    { id: 'account',   label: 'My Account', icon: User,            path: '/profile' },
    { id: 'dashboard', label: 'Dashboard',  icon: LayoutDashboard, path: '/dashboard' },
    { id: 'wishlist',  label: 'Wishlist',   icon: Heart,           path: '/wishlist' },
    ...(user.is_admin ? [{ id: 'admin', label: 'Admin Panel', icon: Shield, path: '/admin' }] : []),
  ] : [];

  return (
    <div ref={wrapperRef}>
      {open && (
        <div
          data-testid="floating-profile-panel"
          className="fixed right-3 bottom-[180px] z-50 w-56 rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 overflow-hidden"
        >
          <div className="px-4 py-2.5 bg-gradient-to-r from-[#1800ad] to-[#3b1ed0] text-white text-xs font-semibold tracking-wide">
            {user ? (user.name || 'Profile') : 'Profile'}
          </div>
          {user ? (
            <div className="py-1">
              {items.map(({ id, label, icon: Icon, path }) => (
                <button
                  key={id}
                  type="button"
                  data-testid={`floating-menu-${id}`}
                  onClick={() => go(path)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-[#0A0B10] hover:bg-[#fff4ed] hover:text-[#1800ad] transition-colors"
                >
                  <Icon size={16} className="text-[#1800ad]" />
                  <span>{label}</span>
                </button>
              ))}
              <div className="my-1 border-t border-gray-100" />
              <button
                type="button"
                data-testid="floating-menu-logout"
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut size={16} />
                <span>Logout</span>
              </button>
            </div>
          ) : (
            <div className="py-1">
              <button
                type="button"
                data-testid="floating-menu-login"
                onClick={() => go('/login')}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-[#1800ad] hover:bg-[#f0edff] transition-colors"
              >
                <User size={16} />
                <span>Login / Sign up</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Profile FAB — shows Google picture if available, initials otherwise. */}
      <button
        type="button"
        data-testid="floating-menu-fab"
        aria-label={user ? 'Open profile menu' : 'Login or sign up'}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-[124px] right-3 z-40 w-12 h-12 rounded-full shadow-xl flex items-center justify-center text-white transition-transform hover:scale-105 active:scale-95 focus:outline-none focus:ring-4 focus:ring-[#1800ad]/30 overflow-hidden"
        style={{
          background: user && !pictureUrl
            ? getAvatarColor(user.user_id || user.name)
            : 'linear-gradient(135deg,#1800ad 0%,#3b1ed0 100%)',
        }}
      >
        {open ? (
          <X size={20} strokeWidth={2.5} />
        ) : user && pictureUrl ? (
          <img
            src={pictureUrl}
            alt={user.name || 'Profile'}
            referrerPolicy="no-referrer"
            className="w-full h-full object-cover"
          />
        ) : user ? (
          <span className="text-sm font-bold tracking-wide">{getInitials(user.name)}</span>
        ) : (
          <User size={20} strokeWidth={2.5} />
        )}
      </button>
    </div>
  );
};

export default FloatingMenuFAB;
